import { QueryClientProvider } from "@tanstack/react-query"
import { ApiClient, createApiClient } from "@taskfortress/core"
import type { ConnectionStatusChangeEvent } from "@taskfortress/core"
import { createContext, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"
import env from "../env"

// Create API client instance with base URL from environment
const apiClient = createApiClient({
  baseUrl: env.api.baseUrl,
})

// Enhanced API context type
interface ApiContextType {
  client: ApiClient
  isConnected: boolean
}

// Create a context for the API client
const ApiContext = createContext<ApiContextType | null>(null)

// Hook to get the API client
export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider")
  }
  return context
}

interface ApiProviderProps {
  children: ReactNode
}

export const ApiProvider = ({ children }: ApiProviderProps) => {
  const [isConnected, setIsConnected] = useState<boolean>(true)

  // Subscribe to connection status changes from the ApiClient
  useEffect(() => {
    // Check initial connection
    apiClient.checkConnection()

    // Update connection state based on ApiClient's status
    setIsConnected(apiClient.isConnected)

    // Listen for connection status changes
    const handleConnectionChange = (status: ConnectionStatusChangeEvent) => {
      setIsConnected(status === "connected")
    }

    apiClient.on("connectionStatusChange", handleConnectionChange)

    // Clean up event listener on unmount
    return () => {
      apiClient.off("connectionStatusChange", handleConnectionChange)
    }
  }, [])

  // Create context value with API client and connection state
  const contextValue = {
    client: apiClient,
    isConnected,
  }

  return (
    <ApiContext.Provider value={contextValue}>
      <QueryClientProvider client={apiClient.queryClient}>
        {children}
      </QueryClientProvider>
    </ApiContext.Provider>
  )
}
